import { Location } from 'history';
import {
  hasNonRecoverableBusinessErrors,
  isLoadingWithNoData,
  isQuoteSaved,
} from 'scenes/common/helpers/apiData';
import { isUnderMaintenance } from 'scenes/common/selectors/configSelectors';
import { FunnelType, LocationState } from 'scenes/common/types';

import {
  LandlordFlowType,
  LandlordFunnelState,
  LandlordStepType,
} from 'scenes/landlord/types';
import { pathToStepType, stepHasProgress } from './progress';

const stepsWithoutPreviousButton = [
  {
    step: LandlordStepType.CoverTypeSelection,
    flow: LandlordFlowType.QuoteDetails,
  },
  {
    step: LandlordStepType.PaymentConfirmation,
    flow: LandlordFlowType.PaymentDetails,
  },
  {
    step: LandlordStepType.ReviewPolicyStartDate,
    flow: LandlordFlowType.ReviewDetails,
  },
];

const stepHasPreviousButton = (
  step: LandlordStepType,
  flow: LandlordFlowType = LandlordFlowType.QuoteDetails
) =>
  !stepsWithoutPreviousButton.find(
    (position) => position.step === step && position.flow === flow
  );

export const hasPreviousButton = (
  location: Location<LocationState<LandlordFlowType>>
) =>
  stepHasPreviousButton(
    pathToStepType(location.pathname),
    location.state?.flow
  );

export const showFlybuysInfoSection = (stepType: LandlordStepType): boolean =>
  stepType === LandlordStepType.FlybuysNumber;

export const showHeaderQuoteInformation = (
  flowType: LandlordFlowType,
  stepType: LandlordStepType
): boolean =>
  (flowType === LandlordFlowType.CoverOptions &&
    stepType !== LandlordStepType.QuotePrice) ||
  (flowType === LandlordFlowType.PolicyDetails &&
    stepType !== LandlordStepType.Summary) ||
  (flowType === LandlordFlowType.PaymentDetails &&
    stepType !== LandlordStepType.PaymentFrequency &&
    stepType !== LandlordStepType.PaymentConfirmation);

export const showFooterQuoteInformation = (
  flowType: LandlordFlowType,
  stepType: LandlordStepType
): boolean =>
  flowType === LandlordFlowType.CoverOptions ||
  (flowType === LandlordFlowType.PolicyDetails &&
    stepType !== LandlordStepType.Summary) ||
  (flowType === LandlordFlowType.PaymentDetails &&
    stepType !== LandlordStepType.PaymentConfirmation);

export const showSaveOnlineInformation = (
  funnelState: LandlordFunnelState
): boolean =>
  !isUnderMaintenance(funnelState.apiResponses.config, FunnelType.Landlord) &&
  funnelState.flow === LandlordFlowType.QuoteDetails &&
  funnelState.step === LandlordStepType.CoverTypeSelection;

export const showHeaderMarketingCampaignInformation = (
  flowType: LandlordFlowType
): boolean => [LandlordFlowType.QuoteDetails].includes(flowType);

export const showSidebarMarketingCampaignInformation = (
  flowType: LandlordFlowType
): boolean =>
  [
    LandlordFlowType.QuoteDetails,
    LandlordFlowType.CoverOptions,
    LandlordFlowType.PolicyDetails,
  ].includes(flowType);

export const showFooterMarketingCampaignInformation = (
  flowType: LandlordFlowType
): boolean =>
  [LandlordFlowType.CoverOptions, LandlordFlowType.PolicyDetails].includes(
    flowType
  );

export const showPaymentConfirmationHeader = (
  flowType: LandlordFlowType,
  stepType: LandlordStepType
) =>
  flowType === LandlordFlowType.PaymentDetails &&
  stepType === LandlordStepType.PaymentConfirmation;

export const showProgressBar = (
  funnelState: LandlordFunnelState,
  location: Location<LocationState<LandlordFlowType>>
) =>
  !hasNonRecoverableBusinessErrors(funnelState.apiResponses.quoteDetails) &&
  !isQuoteSaved(funnelState.apiResponses.quoteDetails) &&
  !isLoadingWithNoData(funnelState.apiResponses.quoteDetails) &&
  !isUnderMaintenance(funnelState.apiResponses.config, FunnelType.Landlord) &&
  stepHasProgress(location);

export const showSidebar = (funnelState: LandlordFunnelState) =>
  !hasNonRecoverableBusinessErrors(funnelState.apiResponses.quoteDetails) &&
  !isQuoteSaved(funnelState.apiResponses.quoteDetails) &&
  !isLoadingWithNoData(funnelState.apiResponses.quoteDetails) &&
  !isUnderMaintenance(funnelState.apiResponses.config, FunnelType.Landlord) &&
  (showSidebarMarketingCampaignInformation(funnelState.flow) ||
    showHeaderQuoteInformation(funnelState.flow, funnelState.step) ||
    showFooterQuoteInformation(funnelState.flow, funnelState.step));

export const showHassleFreeClaims = (
  flowType: LandlordFlowType,
  stepType: LandlordStepType
): boolean =>
  flowType === LandlordFlowType.CoverOptions ||
  (flowType === LandlordFlowType.PolicyDetails &&
    [
      LandlordStepType.PropertyCondition,
      LandlordStepType.HeritageListed,
      LandlordStepType.RewiredOrReplumbed,
      LandlordStepType.PropertyUnoccupied,
      LandlordStepType.PropertyRenovation,
      LandlordStepType.Over5Acres,
      LandlordStepType.PropertyOccupancy,
      LandlordStepType.PolicyHoldersHistory,
      LandlordStepType.MortgageProvider,
    ].includes(stepType));

export const shouldShowClaimCVP = (funnelState: LandlordFunnelState): boolean =>
  funnelState.flow === LandlordFlowType.CoverOptions &&
  funnelState.step === LandlordStepType.QuotePrice;
